import { type FormEvent, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import { WelcomeLayout } from '@/components/auth/WelcomeLayout';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { VersionBadge } from '@/components/layout/VersionBadge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { useLogin, useMe } from '@/lib/auth';
import { usePublicSettings } from '@/lib/settings';

export function LoginPage() {
  const me = useMe();
  const settings = usePublicSettings();
  const login = useLogin();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  if (me.data) return <Navigate to="/" replace />;

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await login.mutateAsync({ email, password });
      navigate('/');
    } catch {
      /* error surfaced below */
    }
  };

  return (
    <WelcomeLayout>
      <AuthCard title="Sign in" subtitle="Welcome back." standalone={false}>
      <form onSubmit={onSubmit} className="space-y-4">
        <FormField label="Email">
          <Input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </FormField>
        <FormField label="Password">
          <PasswordInput
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </FormField>

        {login.isError && (
          <p className="text-sm text-red-600">{(login.error as Error).message}</p>
        )}

        <Button type="submit" className="w-full" disabled={login.isPending}>
          {login.isPending ? 'Signing in…' : 'Sign in'}
        </Button>
        <p className="text-center text-sm">
          <Link to="/forgot-password" className="text-stone-500 dark:text-stone-400 hover:text-forge-700 dark:hover:text-forge-300 hover:underline">
            Forgot password?
          </Link>
        </p>
      </form>
      {settings.data?.signup_mode === 'open' && (
        <p className="mt-6 text-center text-sm text-stone-600 dark:text-stone-400">
          New here?{' '}
          <Link to="/signup" className="font-medium text-forge-700 dark:text-forge-400 hover:underline">
            Create an account
          </Link>
        </p>
      )}
      {settings.data?.signup_mode === 'request' && (
        <p className="mt-6 text-center text-sm text-stone-600 dark:text-stone-400">
          New here?{' '}
          <Link to="/request-account" className="font-medium text-forge-700 dark:text-forge-400 hover:underline">
            Request an account
          </Link>
        </p>
      )}
      </AuthCard>
    </WelcomeLayout>
  );
}

/**
 * The white-card form panel used across every public auth route.
 *
 * By default `AuthCard` renders its own full-page centered layout
 * (with the PartForge logo on top), which is what the short-lived
 * pages (`/forgot-password`, accept-invite errors, etc.) want.
 * Inside `<WelcomeLayout>` — the two-column landing page used by
 * `/login` and `/signup` — pass `standalone={false}` so we render
 * just the bare card and let WelcomeLayout handle layout.
 */
export function AuthCard({
  title,
  subtitle,
  children,
  standalone = true,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  standalone?: boolean;
}) {
  const card = (
    <div className="w-full max-w-sm rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-8 shadow-sm">
      <div className="mb-6 text-center">
        <h2 className="text-xl font-semibold tracking-tight text-stone-900 dark:text-stone-100">
          {title}
        </h2>
        {subtitle && (
          <p className="mt-1 text-sm text-stone-500 dark:text-stone-400">{subtitle}</p>
        )}
      </div>
      {children}
    </div>
  );
  if (!standalone) return card;
  // Lazy import avoids a circular module dep — WelcomeLayout imports
  // the logo asset; this file is imported by every auth page.
  return <CenteredAuthLayoutInline>{card}</CenteredAuthLayoutInline>;
}

/**
 * Tiny wrapper that mirrors the look of `CenteredAuthLayout` from
 * `@/components/auth/WelcomeLayout` but lives in this file to avoid
 * the import cycle (WelcomeLayout depends on AuthCard transitively
 * via Login/Signup). Same visuals: centered card, logo above.
 */
function CenteredAuthLayoutInline({ children }: { children: React.ReactNode }) {
  // eslint-disable-next-line @typescript-eslint/no-require-imports -- vite asset import
  const logoLightUrl = new URL('../assets/partforge-red-transparent.png', import.meta.url).href;
  // eslint-disable-next-line @typescript-eslint/no-require-imports -- vite asset import
  const logoDarkUrl = new URL('../assets/partforge-red-transparent-dark.png', import.meta.url).href;
  return (
    <div className="relative flex min-h-screen items-center justify-center bg-stone-50 dark:bg-stone-900 px-4 py-12">
      <div className="absolute right-4 top-4 z-10">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center">
          <img src={logoLightUrl} alt="PartForge" className="h-[4.5rem] w-auto dark:hidden" />
          <img src={logoDarkUrl} alt="" aria-hidden className="hidden h-[4.5rem] w-auto dark:block" />
        </div>
        {children}
      </div>
      <div className="absolute bottom-3 left-0 right-0 flex justify-center">
        <VersionBadge />
      </div>
    </div>
  );
}

export function FormField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-stone-700 dark:text-stone-300">{label}</span>
      {children}
    </label>
  );
}
